/**
 * The focus-scoped input router's state and the client creation hooks, kept on `globalThis` under ONE
 * `Symbol.for` key. Every copy of `focus-scoped-input.ts` in a page — a duplicate bundle, a module re-evaluated
 * by HMR — reads and writes this same object. With module-local state, the copy that installed the router on
 * `window` would read its own focus set while another copy's `setFocusedClient` wrote a different one: the focus
 * would silently stop routing anything.
 *
 * Internal: not exported from the package. `focus-scoped-input.ts` is the public surface; the initializer runs
 * the creation hooks through `runClientCreationHooks`.
 */
import type { ClientCreationHook, ClientToken } from './focus-scoped-input.js';
import type { MockClient } from './types.js';

export interface FocusScopedInputState {
  /** The client whose entry point is running: a key listener added now belongs to it (`null`: to no client). */
  registeringClient: ClientToken | null;
  /**
   * The client whose call `runAsRegisteringClient` is running (`null` outside one). A key-listener removal is that
   * client's own only while it is also the registering client: inside `setRegisteringClient`'s sticky window other
   * clients' code runs too, so a removal there is attributed to no client.
   */
  callingClient: ClientToken | null;
  /** The clients a key reaches; empty means every client. */
  readonly focusedClients: Set<ClientToken>;
  /** The client each process `scopeProcessInputToClient` scoped belongs to. */
  readonly scopedProcesses: WeakMap<object, ClientToken>;
  /** The systems whose client-called hooks (`onJoinedArea` & co.) already run as their client. */
  readonly scopedSystems: WeakSet<object>;
  /** `registerClientCreationHook`'s registry. */
  readonly clientCreationHooks: Set<ClientCreationHook>;
}

const STATE_KEY = Symbol.for('gotti.test-utils.focus-scoped-input');

export const focusScopedInputState = (): FocusScopedInputState => {
  const holder = globalThis as unknown as Record<symbol, FocusScopedInputState | undefined>;
  const state = (holder[STATE_KEY] ??= {
    registeringClient: null,
    callingClient: null,
    focusedClients: new Set(),
    scopedProcesses: new WeakMap(),
    scopedSystems: new WeakSet(),
    clientCreationHooks: new Set(),
  });
  // A state an older copy of this module created (before scopedSystems existed) gains the field it lacks.
  (state as { scopedSystems?: WeakSet<object> }).scopedSystems ??= new WeakSet();
  return state;
};

/** Calls every registered creation hook with `client`, in registration order; a hook that throws fails the creation. */
export const runClientCreationHooks = (client: MockClient): void => {
  for (const hook of [...focusScopedInputState().clientCreationHooks]) hook(client);
};
